import React, { useEffect, useState, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, avatarSrc } from "@/lib/api";
import { useAppWebSocket } from "@/lib/useAppWebSocket";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  House,
  ChartPieSlice,
  UsersThree,
  ChatsCircle,
  EnvelopeSimple,
  UserCircle,
  SignOut,
  Handshake,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: ChartPieSlice },
  { to: "/network", label: "Network", icon: UsersThree },
  { to: "/feed", label: "Wall", icon: ChatsCircle },
  { to: "/messages", label: "Messages", icon: EnvelopeSimple, key: "messages" },
  { to: "/referrals", label: "Referrals", icon: Handshake },
  { to: "/profile", label: "Profile", icon: UserCircle },
];

// Mobile bottom-nav must stay a single 60px-tall row so viewport-pinned UI
// (e.g. the Messages composer) doesn't get hidden behind a wrapped 2nd row.
// Referrals is reached via the icon button in the mobile topbar instead.
const MOBILE_NAV = NAV.filter((n) => n.to !== "/referrals");

function dispatchOpenPalette() {
  // Programmatic equivalent of pressing ⌘K — fire a synthetic keydown that
  // the CommandPalette listener picks up.
  window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, ctrlKey: true }));
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  const refreshUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/unread/count");
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  }, []);

  useAppWebSocket((data) => {
    if (data?.type === "message.new") refreshUnread();
  }, !!user && typeof user === "object");

  useEffect(() => {
    if (user && typeof user === "object") refreshUnread();
  }, [user, location.pathname, refreshUnread]);

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-ink-900 text-zinc-100 grain">
      {/* Sidebar */}
      <aside
        className="hidden md:flex flex-col w-64 shrink-0 border-r hairline"
        style={{ backgroundColor: "#0d0d11" }}
        data-testid="app-sidebar"
      >
        <div className="px-6 py-7 border-b hairline">
          <div className="flex items-center gap-2">
            <House size={26} weight="duotone" className="text-gold" />
            <div>
              <div className="font-display text-2xl tracking-tight leading-none">Reagent</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1">Network</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          <button
            type="button"
            onClick={dispatchOpenPalette}
            data-testid="palette-trigger"
            className="w-full mb-2 flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-zinc-400 hover:text-white border hairline hover:border-gold/30 transition-all"
          >
            <span className="flex items-center gap-3">
              <MagnifyingGlass size={18} />
              <span className="text-sm">Quick find…</span>
            </span>
            <kbd className="text-[10px] tracking-widest text-zinc-500 border border-white/10 rounded px-1.5 py-0.5">⌘K</kbd>
          </button>
          {NAV.map((item) => {
            const testId = `nav-${item.label.toLowerCase()}`;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={testId}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 relative ${
                    isActive
                      ? "bg-white/5 text-white"
                      : "text-zinc-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-2 bottom-2 w-[2px] bg-gold rounded-full" />
                    )}
                    <item.icon
                      size={20}
                      weight={isActive ? "fill" : "regular"}
                      className={isActive ? "text-gold" : ""}
                    />
                    <span className="text-sm flex-1" data-testid={`${testId}-label`}>{item.label}</span>
                    {item.key === "messages" && unread > 0 && (
                      <span className="text-[9px] uppercase tracking-wider bg-gold text-ink-900 rounded-full px-1.5 py-0.5 font-bold min-w-[18px] text-center" data-testid="unread-badge">
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t hairline">
          {user && typeof user === "object" && (
            <div className="flex items-center gap-3 mb-3" data-testid="sidebar-user">
              <Avatar className="h-10 w-10 border border-white/10">
                <AvatarImage src={avatarSrc(user.avatar_url)} alt={user.name} />
                <AvatarFallback className="bg-ink-700 text-zinc-300">
                  {user.name?.[0] || "A"}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="text-sm truncate">{user.name}</div>
                <div className="text-xs text-zinc-500 truncate">{user.area ? `${user.area}${user.city ? `, ${user.city}` : ""}` : "—"}</div>
              </div>
            </div>
          )}
          <button
            onClick={doLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors py-2 border hairline rounded-md"
          >
            <SignOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile topbar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3 bg-black/70 backdrop-blur-xl border-b hairline">
        <div className="flex items-center gap-2">
          <House size={22} weight="duotone" className="text-gold" />
          <span className="font-display text-xl">Reagent Network</span>
        </div>
        <div className="flex items-center gap-3">
          <NavLink
            to="/referrals"
            data-testid="mobile-topbar-referrals"
            aria-label="Referrals"
            className={({ isActive }) =>
              `relative p-1.5 rounded-md transition-colors ${isActive ? "text-gold bg-gold/10" : "text-zinc-400 hover:text-gold"}`
            }
          >
            <Handshake size={20} weight="duotone" />
          </NavLink>
          <button
            onClick={doLogout}
            data-testid="logout-button-mobile"
            className="text-xs text-zinc-400"
          >
            Sign out
          </button>
        </div>
      </div>

      <main className="flex-1 min-w-0 pt-14 md:pt-0" data-testid="app-main">
        <MobileNav unread={unread} />
        {children}
      </main>
    </div>
  );
}

function MobileNav({ unread = 0 }) {
  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-black/80 backdrop-blur-xl border-t hairline grid grid-cols-5">
      {MOBILE_NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          data-testid={`mobile-nav-${item.label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex flex-col items-center py-3 text-[10px] tracking-wider uppercase ${
              isActive ? "text-gold" : "text-zinc-500"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <div className="relative">
                <item.icon size={20} weight={isActive ? "fill" : "regular"} />
                {item.key === "messages" && unread > 0 && (
                  <span className="absolute -top-1 -right-2 text-[8px] bg-gold text-ink-900 rounded-full px-1 font-bold min-w-[14px] text-center">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </div>
              <span className="mt-1">{item.label}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
